import React, { useEffect, useRef, useState } from "react";
// import { marked } from "marked";
import Navbar from "./navbar/Navbar";
import "./style.css";
// import ProgressBar from "./ProgressBar";
import EditorPanel from "./EditorPanel";
import FullViewOverlay from "./FullViewOverlay";
import { API_URL } from "../creds";
import { toast } from 'react-toastify';
import { useParams } from "react-router-dom";
import BottomBar from "../pages/BottomBar";
import FindReplacePopup from "./FindReplacePopup";
import { useNavigate } from "react-router-dom";

export default function Home() {
  const navigate = useNavigate();
  /* ---------------- STATE ---------------- */
  const [sourceSegments, setSourceSegments] = useState([]);
  const [translationSegments, setTranslationSegments] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);

  const [isSourceEditing, setIsSourceEditing] = useState(false);
  const [isTargetEditing, setIsTargetEditing] = useState(false);

  const [darkMode, setDarkMode] = useState(false);

  const [showSourceDoc, setShowSourceDoc] = useState(false);
  const [showTransDoc, setShowTransDoc] = useState(false);

  const [sourceLang, setSourceLang] = useState("en");
  const [targetLang, setTargetLang] = useState("hi");

  const { taskid } = useParams(); // get taskid from URL

  const [sourceContents, setSourceContents] = useState([]);
  const [targetContent, setTargetContent] = useState([]);

  const [srcFile, setSrcFile] = useState("");
  const [tgtFile, setTgtFile] = useState("");

  const [showFindReplace, setShowFindReplace] = useState(false);
  const textareaRef = useRef(null);

  // Fetch task content if taskid exists
  useEffect(() => {
    if (!taskid) return; // no taskid, skip fetch

    const fetchTaskContent = async () => {
      try {
        const res = await fetch(`${API_URL}/get-markdown-task`, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("access_token")}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ taskid }), // send taskid to backend
        });
        const data = await res.json();

        if (!res.ok) throw new Error(data.detail || "Failed to fetch task");

        // set editor content
        setSourceContents(data.source_content);
        setTargetContent(data.target_content);
        setSourceSegments(splitMarkdown(data.source_content));
        setTranslationSegments(splitMarkdown(data.target_content));
        setCurrentIndex(data.currentIndex);
        setSrcFile(data.src_file)
        setTgtFile(data.tgt_file)
        setSourceLang(data.src_lang)
        setTargetLang(data.tgt_lang)

      } catch (err) {
        console.error(err);
        toast.error(err.message || "Failed to load task content");
      }
    };

    fetchTaskContent();
  }, [taskid]);



  /* ---------------- HELPERS ---------------- */
  const splitMarkdown = (text) =>
    text
      .split(/\n\s*\n/)
      .map((s) => s.trim())
      .filter(Boolean);



  /* ---------------- EDITING ---------------- */
  const updateSource = (val) => {
    const copy = [...sourceSegments];
    copy[currentIndex] = val;
    setSourceSegments(copy);
  };

  const updateTarget = (val) => {
    const copy = [...translationSegments];
    copy[currentIndex] = val;
    setTranslationSegments(copy);
  };

  /* ---------------- NAVIGATION ---------------- */
  const selectSegment = (i) => {
    setCurrentIndex(i);
    setIsSourceEditing(false);
    setIsTargetEditing(false);
  };

  const goNext = () => {
    if (currentIndex < sourceSegments.length - 1)
      selectSegment(currentIndex + 1);
  };

  const goPrev = () => {
    if (currentIndex > 0) selectSegment(currentIndex - 1);
  };

  /* ---------------- DOWNLOAD ---------------- */
  const downloadTranslation = () => {
    if (!translationSegments.length) {
      alert("No translation content");
      return;
    }
    const blob = new Blob(
      [translationSegments.join("\n\n")],
      { type: "text/markdown" }
    );
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "translated.md";
    a.click();
    URL.revokeObjectURL(url);
  };

  /* ---------------- EFFECTS ---------------- */
  useEffect(() => {
    document.body.classList.toggle("dark-mode", darkMode);
  }, [darkMode]);

  /* ---------------- DERIVED ---------------- */
  const activeSource = sourceSegments[currentIndex] || "";
  const activeTarget = translationSegments[currentIndex] || "";

  const rtlSource = sourceLang === "ur" ? "rtl-mode" : "";
  const rtlTarget = targetLang === "ur" ? "rtl-mode" : "";


  const saveTargetToServer = async () => {
    if (!translationSegments.length) {
      toast("Nothing to save", {
        style: { background: '#fef3c7', color: '#92400e' },
      })
      return
    }

    const toastId = toast.loading("Saving your translation...", {
      position: "top-right",
      autoClose: false, // important – prevent auto-dismiss during loading
    });

    try {
      const res = await fetch(`${API_URL}/mde-save-file`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("access_token")}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          content: translationSegments.join("\n\n"),
          taskid: taskid,
          currentIndex: currentIndex,
          src_file: srcFile,
          tgt_file: tgtFile,
          status: "Under Progress"
        }),
      })

      const data = await res.json()


      if (!res.ok) throw new Error(data.detail || "Save failed")

      toast.update(toastId, {
        render: "Saved successfully!",
        type: "success",
        isLoading: false,
        autoClose: 5000,
        closeButton: true,
        position: "top-right",
      });

    } catch (err) {
      console.error(err)
      // Replace loading → error
      toast.update(toastId, {
        render: err.message || "Failed to save",
        type: "error",
        isLoading: false,
        autoClose: 7000,
        closeButton: true,
        hideProgressBar: true,
        position: "top-right",
      });
    }
  }

  const saveTargetToServerFinish = async () => {
    if (!translationSegments.length) {
      toast("Nothing to save", {
        style: { background: '#fef3c7', color: '#92400e' },
      })
      return
    }

    const confirmed = window.confirm("Are you sure you want to finish this task?\n")

    if (!confirmed) {
      toast("Action cancelled", {
        icon: "ℹ️",
        style: { background: '#e0f2fe', color: '#1e40af' },
      });
      return;
    }

    const toastId = toast.loading("Saving your translation...", {
      position: "top-right",
      autoClose: false,
    });

    try {
      const res = await fetch(`${API_URL}/mde-save-file`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("access_token")}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          content: translationSegments.join("\n\n"),
          taskid: taskid,
          currentIndex: currentIndex,
          src_file: srcFile,
          tgt_file: tgtFile,
          status: "Completed"
        }),
      })

      const data = await res.json()


      if (!res.ok) throw new Error(data.detail || "Save failed")

      toast.update(toastId, {
        // render: "File Saved/Finished. Now click on back button to retum dashboard/Home Screen",
        render: "File Saved/Finished.",
        type: "success",
        isLoading: false,
        autoClose: 5000,
        closeButton: true,
        position: "top-right",
      });

      // Go back after success 
       setTimeout(() => { navigate("-1"); }, 1000);

    } catch (err) {
      console.error(err)
      // Replace loading → error
      toast.update(toastId, {
        render: err.message || "Failed to save",
        type: "error",
        isLoading: false,
        autoClose: 7000,
        closeButton: true,
        hideProgressBar: true,
        position: "top-right",
      });
    } finally { }
  }

  /* ================= RENDER ================= */
  return (
    <div className="mt-3 bg-gradient-to-b from-gray-50 to-white">
      {/* <ProgressBar sourceSegments={sourceSegments} currentIndex={currentIndex} /> */}
      {/* NAVBAR */}
      <Navbar
        showSourceDoc={showSourceDoc}
        setShowSourceDoc={setShowSourceDoc}
        showTransDoc={showTransDoc}
        setShowTransDoc={setShowTransDoc}
        darkMode={darkMode}
        setDarkMode={setDarkMode}
        downloadTranslation={downloadTranslation}
        onSaveFile={saveTargetToServerFinish}
        srcLang={sourceLang}
        tgtLang={targetLang}
        setShowFindReplace={setShowFindReplace}
      />

      {/* PROGRESS */}

      {(!showSourceDoc || !showTransDoc) && (
        <div>
          <EditorPanel
            /* state */
            sourceSegments={sourceSegments}
            currentIndex={currentIndex}
            isSourceEditing={isSourceEditing}
            isTargetEditing={isTargetEditing}
            activeSource={activeSource}
            activeTarget={activeTarget}
            rtlSource={rtlSource}
            rtlTarget={rtlTarget}
            /* setters / handlers */
            setIsSourceEditing={setIsSourceEditing}
            setIsTargetEditing={setIsTargetEditing}
            selectSegment={selectSegment}
            goPrev={goPrev}
            goNext={goNext}
            updateSource={updateSource}
            updateTarget={updateTarget}
            onSaveFile={saveTargetToServer}
            textareaRef={textareaRef}
          />
        </div>
      )}
      <div>

        {/* Overlay AFTER navbar */}
        <FullViewOverlay
          showSource={showSourceDoc}
          showTarget={showTransDoc}
          sourceContents={sourceContents}
          targetContents={targetContent}
          onClose={() => {
            setShowSourceDoc(false);
            setShowTransDoc(false);
          }}

          rtlSource={rtlSource}
          rtlTarget={rtlTarget}
        />
      </div>
      <BottomBar />

      <FindReplacePopup
        setShowFindReplace={setShowFindReplace}
        showFindReplace={showFindReplace}
        targetSegments={translationSegments}
        setTargetSegments={setTranslationSegments}
        textareaRef={textareaRef}
      />

    </div>
  );
}
